import { useState, useEffect } from 'react'
import { DataTable, PaginationWrapper } from '@/components/organisms'
import { Card, CardContent } from '@/components/ui/card'
import { TableSkeleton } from '@/components/ui/table-skeleton'
import { formatCurrency } from '@/utils'
import { formatDate } from '@/utils/date'
import { Project } from '@/types/project'
import FundingFilters from '@/components/filters/FundingFilters'

interface FundingRoundsTableProps {
  title: string
  navigate: (path: string) => void
  useDataHook: (params: Record<string, any>) => any
  dataKey: string
  showPagination?: boolean
  useUrlParams?: boolean
}

const ProjectLogo = ({ logo, alt }: { logo?: string | null; alt: string }) => {
  const [imgError, setImgError] = useState(false)
  return (
    <img
      src={!imgError && logo ? logo : '/placeholder-logo.png'}
      alt={alt}
      className="w-8 h-8 rounded-full object-cover"
      onError={() => setImgError(true)}
    />
  )
}

const FundingRoundsTable = ({ 
  title, 
  navigate, 
  useDataHook, 
  dataKey: _dataKey, 
  showPagination = true,
  useUrlParams: _useUrlParams = false 
}: FundingRoundsTableProps) => {
  const dataHookResult = useDataHook({})
  
  // Get data from useFundingRoundsPagination hook
  const {
    projects,
    loading,
    error: _error,
    pagination,
    search,
    sortBy,
    sortDirection,
    setSearch,
    setSortBy,
    setSortDirection,
    goToPage
  } = dataHookResult

  // Local state for search input to prevent input reset on debounced updates
  const [localSearch, setLocalSearch] = useState(search || '')

  // Only sync local search with hook's search value on initial mount
  // After that, localSearch is independent and won't be reset by debounced updates
  useEffect(() => {
    setLocalSearch(search || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  const handleSearchChange = (value: string) => {
    setLocalSearch(value) // Update local state immediately
    setSearch(value) // Pass to debounced hook handler
  }

  const handleProjectClick = (project: Project) => {
    navigate(`/data-rooms/${project.project_id}`)
  }

  const columns = [
    {
      key: 'project_name',
      title: 'Project',
      width: 'w-[400px]',
      render: (item: Project) => (
        <div className="flex items-center gap-[10px]">
          <div className="bg-[var(--base-muted,#f5f5f5)] dark:bg-[#2a2a2a] overflow-hidden rounded-full shrink-0 size-[26px]">
            <ProjectLogo logo={item.logo} alt={item.project_name} />
          </div>
          <div className="font-['Plus_Jakarta_Sans'] font-medium text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px] overflow-hidden text-ellipsis whitespace-nowrap">
            {item.project_name}
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'total_raised',
      title: 'Total Raised',
      width: 'w-[300px]',
      render: (item: Project) => {
        const totalRaised = (item.rounds || [])
          .reduce((sum, round) => sum + round.amount_raised, 0)
        return (
          <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
            {formatCurrency(totalRaised)}
          </span>
        )
      },
      sortable: true
    },
    {
      key: 'latest_round',
      title: 'Latest Round',
      width: 'w-[300px]',
      render: (item: Project) => {
        const latestRound = (item.rounds || [])
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
        return (
          <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
            {latestRound ? formatCurrency(latestRound.amount_raised) : '—'}
          </span>
        )
      },
      sortable: true
    },
    {
      key: 'valuation',
      title: 'Valuation',
      width: 'w-[300px]',
      render: (item: Project) => {
        const currentValuation = (item.rounds || [])
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
        return (
          <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
            {currentValuation ? formatCurrency(currentValuation.fully_diluted_valuation) : '—'}
          </span>
        )
      },
      sortable: true
    },
    {
      key: 'date',
      title: 'Date',
      width: 'w-[276px]',
      render: (item: Project) => {
        const latestRound = (item.rounds || [])
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0]
        return (
          <span className="font-['Plus_Jakarta_Sans'] font-normal text-[#2d2923] dark:text-[#fdfdfd] text-[14px] leading-[20px]">
            {latestRound ? formatDate(latestRound.date) : '—'}
          </span>
        )
      },
      sortable: true
    }
  ]

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between h-[52px]">
        <h3 className="font-['Plus_Jakarta_Sans'] font-semibold leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] text-[23px] tracking-[-0.2px]">
          {title}
        </h3>
      </div>

      {/* Table Container with Figma styling */}
      <div className="bg-[rgba(255,255,255,0.32)] dark:bg-[rgba(28,28,28,0.32)] border border-white dark:border-[rgba(255,255,255,0.1)] rounded-none overflow-hidden">
        {/* Filters */}
        <div className="bg-[rgba(255,255,255,0.44)] dark:bg-[rgba(28,28,28,0.44)] px-[8px] py-[8px]">
          <FundingFilters
            search={localSearch}
            onSearchChange={handleSearchChange}
            sortBy={sortBy || ''}
            onSortByChange={(value) => setSortBy(value || 'ORDERS')}
            sortDirection={sortDirection || ''}
            onSortDirectionChange={(value) => setSortDirection(value || 'DESC')}
          />
        </div>

        {/* Table */}
        <div className="bg-[rgba(255,255,255,0.2)] dark:bg-[rgba(28,28,28,0.2)] overflow-clip">
          {loading ? (
            <TableSkeleton 
              variant="elevated"
              rows={5}
              columns={5}
              showHeader={false}
              showFilters={false}
            />
          ) : (
            <DataTable
              columns={columns}
              data={projects || []}
              keyExtractor={(item) => item.project_id}
              isLoading={false}
              onRowClick={handleProjectClick}
            />
          )}
        </div>
      </div>
      
      {/* Pagination */}
      {showPagination && pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center">
          <PaginationWrapper
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={goToPage}
          />
        </div>
      )}
      
      {/* Information about number of records */}
      <div className="text-center text-sm text-[var(--muted-foreground)]">
        Showing {projects?.length || 0} of {pagination?.totalItems || 0} projects
      </div>
    </div>
  )
}

export default FundingRoundsTable 